import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query'
import { CameraListParams, cameraApi } from '@/api/cameraApi'

export const cameraQueryKeys = {
  all: ['cameras'] as const,
  list: (params?: CameraListParams) =>
    [...cameraQueryKeys.all, 'list', params] as const,
  details: (deviceId: string, cameraId: string) =>
    [...cameraQueryKeys.all, 'details', deviceId, cameraId] as const,
  cameraCreationStatus: (deviceId: string, pollTaskId: string) =>
    [
      ...cameraQueryKeys.all,
      'camera-creation-status',
      deviceId,
      pollTaskId
    ] as const
}

export const useGetCameraList = (
  params: CameraListParams,
  options?: { keepPreviousData?: boolean }
) => {
  return useQuery({
    queryKey: cameraQueryKeys.list(params),
    queryFn: () => cameraApi.getCameraList(params),
    placeholderData: options?.keepPreviousData ? keepPreviousData : undefined
  })
}

type UseGetCameraDetailsParams = {
  deviceId: string
  cameraId: string
  enabled?: boolean
}

export const useGetCameraDetails = ({
  deviceId,
  cameraId,
  enabled
}: UseGetCameraDetailsParams) => {
  return useQuery({
    queryKey: cameraQueryKeys.details(deviceId, cameraId),
    queryFn: () => cameraApi.getCameraDetails({ deviceId, cameraId }),
    enabled
  })
}

export const useCreateCameras = () => {
  return useMutation({
    mutationFn: cameraApi.createCameras
  })
}

type UsePollCameraCreationStatusParams = {
  deviceId: string
  pollTaskId: string
  enabled?: boolean
}

export const usePollCameraCreationStatus = ({
  deviceId,
  pollTaskId,
  enabled
}: UsePollCameraCreationStatusParams) => {
  return useQuery({
    queryKey: cameraQueryKeys.cameraCreationStatus(deviceId, pollTaskId),
    queryFn: () => cameraApi.getCameraCreationStatus(deviceId),
    refetchInterval: (query) => {
      const { data, error } = query.state

      return data?.status === 'Complete' || data?.status === 'Failed' || error
        ? false
        : 3000
    },
    enabled
  })
}

export const useDeleteCamera = () => {
  return useMutation({
    mutationFn: cameraApi.deleteCamera
  })
}

export const useRefreshCameraImage = () => {
  return useMutation({
    mutationFn: cameraApi.refreshCameraImage
  })
}

export const usePingCamera = () => {
  return useMutation({
    mutationFn: cameraApi.pingCamera
  })
}

export const useGetImportCameras = () => {
  return useMutation({
    mutationFn: cameraApi.getImportCameras
  })
}

export const useCheckIntegrationUrl = () => {
  return useMutation({
    mutationFn: cameraApi.checkIntegrationUrl
  })
}
